import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDistribution } from "@/hooks/useDistribution";
import { useCreateExam } from "@/hooks/useStudent";
import { L } from "@/i18n";

const EMPTY = { module: "", exam_date: "", exam_time: "", location: "", location_link: "", notes: "" };
const input = "w-full h-10 rounded-md bg-[#0f172a] border border-slate-700 px-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 [color-scheme:dark]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block mb-1.5 text-xs font-medium text-slate-300">{label}</span>
    {children}
  </label>
);

export const ScheduleExamDialog = ({ open, onOpenChange, user }) => {
  const { data } = useDistribution();
  const create = useCreateExam();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const modules = data?.modules ?? [];

  const close = (o) => {
    if (create.isPending) return;
    if (!o) { setForm(EMPTY); setError(""); }
    onOpenChange(o);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.module || !form.exam_date) return setError(L("Choose the module and exam date.", "Escolha o módulo e a data do exame."));
    const module_name = modules.find((m) => m.id === form.module)?.name ?? "";
    create.mutate({ userId: user.id, username: user.username, exam: { ...form, module_name } }, { onSuccess: () => close(false) });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#131f37] border-[#1e293b] text-slate-200 sm:max-w-lg" data-testid="schedule-exam-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-50">{L("Schedule exam", "Agendar exame")}</DialogTitle>
          <DialogDescription className="text-slate-400">{L("New exam assigned to", "Novo exame atribuído a")} <b className="text-slate-200">{user.username}</b>.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field label={L("Module *", "Módulo *")}>
            <Select value={form.module} onValueChange={set("module")}>
              <SelectTrigger className="h-10 bg-[#0f172a] border-slate-700 text-slate-200" data-testid="exam-module-select"><SelectValue placeholder={L("Select module", "Selecionar módulo")} /></SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700 text-slate-200 max-h-72">
                {modules.map((m) => <SelectItem key={m.id} value={m.id} className="focus:bg-slate-800 focus:text-slate-50" data-testid={`exam-module-option-${m.id}`}>{m.id} · {m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={L("Date *", "Data *")}><input type="date" className={input} value={form.exam_date} onChange={set("exam_date")} data-testid="exam-date-input" /></Field>
            <Field label={L("Time", "Hora")}><input type="time" className={input} value={form.exam_time} onChange={set("exam_time")} data-testid="exam-time-input" /></Field>
          </div>
          <Field label={L("Location", "Local")}><input className={input} value={form.location} onChange={set("location")} placeholder={L("e.g. Lisbon", "Ex.: Lisboa")} data-testid="exam-location-input" /></Field>
          <Field label={L("Map link", "Link do mapa")}><input className={input} value={form.location_link} onChange={set("location_link")} placeholder="https://maps.app.goo.gl/…" data-testid="exam-location-link-input" /></Field>
          <Field label={L("Notes", "Notas")}><textarea rows={2} className={`${input} h-auto py-2`} value={form.notes} onChange={set("notes")} data-testid="exam-notes-input" /></Field>
          {error && <p className="text-sm text-rose-300" data-testid="exam-form-error">{error}</p>}
          <DialogFooter>
            <button type="button" onClick={() => close(false)} className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800" data-testid="exam-cancel-button">{L("Cancel", "Cancelar")}</button>
            <button type="submit" disabled={create.isPending} className="flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-[#0a1425] hover:bg-amber-400 disabled:opacity-60" data-testid="exam-submit-button">
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {L("Schedule exam", "Agendar exame")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
